import { fetchWithAuth } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL || '';

/** Build error message for 409 (duplicate name) from response body or default. */
async function getErrorMessageForConflict(response, defaultMessage) {
  try {
    const text = await response.text();
    const data = text ? JSON.parse(text) : {};
    return data.message || data.msg || defaultMessage;
  } catch (_) {
    return defaultMessage;
  }
}

class EquipmentPageService {
  /** Get all machines – equipmentCategoryId 10001 for Machine */
  async getMachines(equipmentCategoryId = 10001) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/machine/get-all-machines?equipmentCategoryId=${equipmentCategoryId}`,
        { method: 'GET' }
      );
      if (!response.ok) throw new Error(`Failed to fetch machines (${response.status})`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching machines:', error);
      throw error;
    }
  }

  /** Add machine */
  async addMachine(machineName, equipmentCategoryId = 10001) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/machine/add-machine`,
        {
          method: 'POST',
          body: JSON.stringify({ machineName, equipmentCategoryId }),
        }
      );
      if (!response.ok) {
        if (response.status === 409) {
          const message = await getErrorMessageForConflict(
            response,
            'A machine with this name already exists. Please use a different name.'
          );
          throw new Error(message);
        }
        throw new Error(`Failed to add machine (${response.status})`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding machine:', error);
      throw error;
    }
  }

  /** Edit machine */
  async editMachine(machineId, machineName, equipmentCategoryId = 10001) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/machine/edit-machine`,
        {
          method: 'PUT',
          body: JSON.stringify({ machineId, machineName, equipmentCategoryId }),
        }
      );
      if (!response.ok) {
        if (response.status === 409) {
          const message = await getErrorMessageForConflict(
            response,
            'A machine with this name already exists. Please use a different name.'
          );
          throw new Error(message);
        }
        throw new Error(`Failed to update machine (${response.status})`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating machine:', error);
      throw error;
    }
  }

  /** Delete machine */
  async deleteMachine(machineId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/machine/delete-machine?machineId=${machineId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error(`Failed to delete machine (${response.status})`);
      return await response.json();
    } catch (error) {
      console.error('Error deleting machine:', error);
      throw error;
    }
  }

  /** Get all tools & tackles – equipmentCategoryId 10002 for Tools & Tackles */
  async getToolsAndTackles(equipmentCategoryId = 10002) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/tools-tackles/get-all-tools-tackles?equipmentCategoryId=${equipmentCategoryId}`,
        { method: 'GET' }
      );
      if (!response.ok) throw new Error(`Failed to fetch tools and tackles (${response.status})`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching tools and tackles:', error);
      throw error;
    }
  }

  /** Add tools & tackles */
  async addToolsAndTackles(toolsTacklesName, equipmentCategoryId = 10002) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/tools-tackles/add-tools-tackles`,
        {
          method: 'POST',
          body: JSON.stringify({ toolsTacklesName, equipmentCategoryId }),
        }
      );
      if (!response.ok) {
        if (response.status === 409) {
          const message = await getErrorMessageForConflict(
            response,
            'An item with this name already exists. Please use a different name.'
          );
          throw new Error(message);
        }
        throw new Error(`Failed to add tools & tackles (${response.status})`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding tools & tackles:', error);
      throw error;
    }
  }

  /** Edit tools & tackles */
  async editToolsAndTackles(toolsTacklesId, toolsTacklesName, equipmentCategoryId = 10002) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/tools-tackles/edit-tools-tackles`,
        {
          method: 'PUT',
          body: JSON.stringify({ toolsTacklesId, toolsTacklesName, equipmentCategoryId }),
        }
      );
      if (!response.ok) {
        if (response.status === 409) {
          const message = await getErrorMessageForConflict(
            response,
            'An item with this name already exists. Please use a different name.'
          );
          throw new Error(message);
        }
        throw new Error(`Failed to update tools & tackles (${response.status})`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating tools & tackles:', error);
      throw error;
    }
  }

  /** Delete tools & tackles */
  async deleteToolsAndTackles(toolsTacklesId) {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/tools-tackles/delete-tools-tackles?toolsTacklesId=${toolsTacklesId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error(`Failed to delete tools & tackles (${response.status})`);
      return await response.json();
    } catch (error) {
      console.error('Error deleting tools & tackles:', error);
      throw error;
    }
  }
}

export default new EquipmentPageService();
